/**
 * <s-sticky-buy> shows its [data-sticky-bar] (the phone-only bottom "Get my $1 glove" button)
 * once the shopper has scrolled past [data-step-one] (product Step 1, or the homepage hero), while the
 * optional main [data-main-buy] button is off screen, and until [data-sticky-end] (just above the footer) comes into view.
 * Elements are looked up on every check so product-form partial re-renders are picked up.
 */
class SStickyBuy extends HTMLElement {
  #ac;
  #frame = 0;
  #picked = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal, passive: true };
    const schedule = () => {
      if (this.#frame) return;
      this.#frame = requestAnimationFrame(() => {
        this.#frame = 0;
        this.#update();
      });
    };
    window.addEventListener('scroll', schedule, opts);
    window.addEventListener('resize', schedule, opts);
    // A button marked [data-scroll-until-picked="<id>"] scrolls to that element (Step 1) until the shopper
    // has changed any choice in the product form, so a first tap never adds a size nobody picked.
    this.addEventListener('change', (event) => {
      if (event.target instanceof Element && event.target.closest('[data-product-form]')) this.#picked = true;
    }, { signal: this.#ac.signal });
    this.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-scroll-until-picked]') : null;
      if (!button || this.#picked) return;
      const target = document.getElementById(button.getAttribute('data-scroll-until-picked'));
      if (!target) return;
      event.preventDefault();
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    }, { signal: this.#ac.signal });
    // FAQ answers past the first two start closed so phones scroll less; wider screens open them all, as before.
    if (window.matchMedia('(min-width: 768px)').matches) {
      for (const item of this.querySelectorAll('details[data-faq-desktop-open]')) item.open = true;
    }
    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  #update() {
    const bar = this.querySelector('[data-sticky-bar]');
    if (!bar) return;
    const stepOne = this.querySelector('[data-step-one]');
    const mainBuy = this.querySelector('[data-main-buy]');
    const end = this.querySelector('[data-sticky-end]');
    const viewport = window.innerHeight;

    const pastStepOne = stepOne ? stepOne.getBoundingClientRect().bottom < 0 : false;
    let mainVisible = false;
    if (mainBuy) {
      const rect = mainBuy.getBoundingClientRect();
      mainVisible = rect.bottom > 0 && rect.top < viewport;
    }
    const endVisible = end ? end.getBoundingClientRect().top < viewport : false;
    // [data-main-buy] is optional: the homepage has none, so the bar shows from the end of the hero until the footer.
    // On the product page the bar waits until a size is picked, so it never buys a size nobody chose.
    const productForm = this.querySelector('[data-product-form]');
    const ready = !productForm || productForm.hasAttribute('data-size-picked');
    const show = ready && pastStepOne && !mainVisible && !endVisible;

    bar.hidden = !show;
  }
}

if (!customElements.get('s-sticky-buy')) customElements.define('s-sticky-buy', SStickyBuy);
